import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, take } from 'rxjs/operators';

import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoresHelpers } from 'idt-ecommerce-api/stores/stores.helpers';
import { StoresService } from 'idt-ecommerce-api/stores/stores.service';
import { RenderPageService } from 'render/services/render-page.service';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';

import { initialRenderState, RenderState } from './render-state.models';


@Injectable()
export class RenderStateService {

  private readonly renderStates: Record<string, BehaviorSubject<RenderState>> = {};

  constructor(
    private readonly contextService: ContextService,
    private readonly renderPageService: RenderPageService,
    private readonly storesService: StoresService
  ) { }

  public getComponents$(slug: string): Observable<IdtComponent[]> {
    return this.getStateBySlug$(slug).pipe(map(({ components }) => components));
  }

  public getCountry$(slug: string): Observable<string | null> {
    return this.getStateBySlug$(slug).pipe(map(({ country }) => country));
  }

  public getDevice$(slug: string): Observable<string | null> {
    return this.getStateBySlug$(slug).pipe(map(({ device }) => device));
  }

  public getLanguage$(slug: string): Observable<string | null> {
    return this.getStateBySlug$(slug).pipe(map(({ lang }) => lang));
  }

  public getTimeTravelTo$(slug: string): Observable<string | null> {
    return this.getStateBySlug$(slug).pipe(map(({ timeTravelTo }) => timeTravelTo));
  }

  public getUserFlags$(slug: string): Observable<string | null> {
    return this.getStateBySlug$(slug).pipe(map(({ userFlags }) => userFlags));
  }

  public getLayout$(slug: string): Observable<IdtComponent | undefined> {
    return this.getStateBySlug$(slug).pipe(
      map(({ components }) => components.find((component) => component.id === '1'))
    );
  }

  public getSlotComponents$(slug: string, parentId: string, slot: string): Observable<IdtComponent[]> {
    return this.getStateBySlug$(slug).pipe(
      map(({ components }) => {
        const parent = components.find((it) => it.id === parentId);
        return parent?.children[slot]?.map(
          (childrenId) => components.find(({ id }) => id === childrenId)
        ).filter(
          (component): component is IdtComponent => component !== undefined
        ) ?? [];
      })
    );
  }

  public initPage(payload: {
    slug: string;
    country: string;
    versionId: string;
    lang: string;
    timeTravelTo?: string;
    userFlags?: string;
  }): Promise<void> {
    return new Promise((resolve) => {
      const slug = payload.slug;
      this.renderPageService.pageBySlug(
        slug,
        payload.country,
        payload.versionId,
        payload.lang,
        undefined,
        payload.timeTravelTo,
        payload.userFlags
      ).pipe(
        take(1)
      ).subscribe((page) => {
        if (page === undefined) {
          console.warn(`Page with slug ${slug} not found`);
        } else {
          this.setStoreInfo(slug, {
            ...payload,
            components: Object.values(page.content)
          });
        }
        resolve();
      });
    });
  }


  public setStoreInfo(slug: string, payload: Partial<RenderState>): void {
    this.updateStateBySlug(slug, payload);
    this.storesService.stores().pipe(
      take(1)
    ).subscribe((stores) => {
      const selectedStore = payload.country ? StoresHelpers.getStoreByCountryCode(stores, payload.country) : undefined;

      if (selectedStore !== undefined) {
        const lang = payload.lang ? StoresHelpers.getSupportedLanguageByCode(selectedStore, payload.lang) : undefined;
        this.contextService.setAllStores(stores);
        this.contextService.initWithStoreAndLanguage({
          storeId: selectedStore.id,
          languageId: lang?.id ?? selectedStore?.supportedLanguages[0].id
        });
      }
    });
  }

  public updateComponents(slug: string, newComponents: IdtComponent[]): void {
    this.getStateBySlug$(slug).pipe(
      first()
    ).subscribe(({ components }) => {
      this.updateStateBySlug(slug, {
        components: [...components, ...newComponents]
      });
    });
  }

  private getStateBySlug$(slug: string): Observable<RenderState> {
    this.initRenderStateByDefault(slug);
    return this.renderStates[slug];
  }

  private updateStateBySlug(slug: string, payload: Partial<RenderState>): void {
    this.initRenderStateByDefault(slug);
    this.renderStates[slug].next({
      ...this.renderStates[slug].getValue(),
      ...payload
    });
  }


  private initRenderStateByDefault(slug: string): void {
    if (!(slug in this.renderStates)) {
      this.renderStates[slug] = new BehaviorSubject(initialRenderState);
    }
  }

}
